import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FileUp,
  History,
  LogOut,
  Menu,
  X,
  Building2,
  AlertTriangle,
  BookOpen,
  SlidersHorizontal,
  FileText,
  Settings,
  Users,
  Bell,
  Plug,
  ScrollText,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { Button } from './ui/button';
import { TimezoneSelector } from './TimezoneSelector';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdminUser, signOut } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/account')
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // ── Section 1: WORKSPACE — everyday operative pages ──
  const workspaceItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, testid: 'nav-dashboard', exact: true },
    { name: 'New IFC Validation', path: '/dashboard', icon: FileUp, testid: 'nav-new-audit' },
    { name: 'Projects', path: '/projects', icon: Building2, testid: 'nav-projects' },
    { name: 'AIM Register History', path: '/history', icon: History, testid: 'nav-history' },
    { name: 'HITL Review Queue', path: '/hitl', icon: AlertTriangle, testid: 'nav-hitl' },
    { name: 'Recent AI Jobs', path: '/ai-jobs', icon: Sparkles, testid: 'nav-ai-jobs' },
  ];

  // ── Section 2: STANDARDS & RULES — knowledge / configuration ──
  const standardsItems = [
    { name: 'Standards Library', path: '/standards', icon: BookOpen, testid: 'nav-standards' },
    { name: 'Validation Profiles', path: '/profiles', icon: SlidersHorizontal, testid: 'nav-profiles' },
    { name: 'BEP Library', path: '/bep-library', icon: FileText, testid: 'nav-bep-library' },
  ];

  // ── Section 3: SYSTEM — settings + admin (collapsible) ──
  const settingsChildren = [
    {
      name: 'Account / Profile',
      path: '/account/profile',
      icon: UserRound,
      testid: 'nav-account-profile',
    },
    {
      name: 'Team Management',
      path: '/admin',
      icon: Users,
      adminOnly: true,
      testid: 'nav-team',
    },
    {
      name: 'Notifications',
      path: '/settings/notifications',
      icon: Bell,
      placeholder: true,
      testid: 'nav-notifications',
    },
    {
      name: 'Integrations & API',
      path: '/settings/integrations',
      icon: Plug,
      placeholder: true,
      adminOnly: true,
      testid: 'nav-integrations',
    },
    {
      name: 'Audit Log',
      path: '/settings/audit-log',
      icon: ScrollText,
      placeholder: true,
      adminOnly: true,
      testid: 'nav-audit-log',
    },
  ];

  const renderNavLink = (item) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + '/');

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setIsMobileOpen(false)}
        data-testid={item.testid}
        className={`
          flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
          transition-colors
          ${
            isActive
              ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] shadow-sm'
              : 'text-white/85 hover:bg-white/10 hover:text-white'
          }
        `}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">{item.name}</span>
      </Link>
    );
  };

  // Section header — uppercase muted label above each group
  const SectionLabel = ({ children, testid }) => (
    <p
      data-testid={testid}
      className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/55"
    >
      {children}
    </p>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-card border border-border shadow-lg"
        data-testid="sidebar-mobile-toggle"
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar — solid PMS 533 C navy panel matching the AHA design ref.
          The new look has the entire rail as a single colour block (no
          right-side border against the page background) and white-tint
          text on navy, with Carolina-blue active-state pills. */}
      <aside
        data-testid="sidebar-root"
        className={`
          fixed top-0 left-0 z-40 h-screen w-64
          bg-[hsl(var(--primary))] text-white
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 shadow-lg
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="px-6 py-5 border-b border-white/10">
            <h1 className="text-2xl font-bold text-white">
              <span className="text-[hsl(var(--accent))]">AHA</span>
            </h1>
            <p className="text-xs text-white/65 mt-1">
              Autonomous AI Handover Agent
            </p>
          </div>

          {/* Navigation — scrollable, three sections */}
          <nav
            className="flex-1 overflow-y-auto px-3 py-2"
            data-testid="sidebar-nav"
          >
            {/* — Section 1: Workspace — */}
            <SectionLabel testid="section-label-workspace">Workspace</SectionLabel>
            <div className="space-y-0.5">
              {workspaceItems.map(renderNavLink)}
            </div>

            <div className="my-2 border-t border-white/10" />

            {/* — Section 2: Standards & Rules — */}
            <SectionLabel testid="section-label-standards">Standards & Rules</SectionLabel>
            <div className="space-y-0.5">
              {standardsItems.map(renderNavLink)}
            </div>
          </nav>

          {/* — Section 3: System — pinned just above user card — */}
          <div
            className="border-t border-white/10 px-3 py-2"
            data-testid="sidebar-system-section"
          >
            <SectionLabel testid="section-label-system">System</SectionLabel>
            <button
              onClick={() => setSettingsOpen((v) => !v)}
              data-testid="nav-settings-toggle"
              aria-expanded={settingsOpen}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-white/85 hover:bg-white/10 hover:text-white transition-colors"
            >
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">Settings</span>
              {settingsOpen ? (
                <ChevronDown className="h-4 w-4 text-white/55" />
              ) : (
                <ChevronRight className="h-4 w-4 text-white/55" />
              )}
            </button>
            {settingsOpen && (
              <div
                className="ml-6 mt-1 space-y-0.5 border-l border-white/15 pl-2"
                data-testid="settings-submenu"
              >
                {settingsChildren
                  .filter((c) => !c.adminOnly || isAdminUser)
                  .map((c) => {
                    const Icon = c.icon;
                    const isActive =
                      location.pathname === c.path ||
                      location.pathname.startsWith(c.path + '/');
                    return (
                      <Link
                        key={c.path}
                        to={c.path}
                        onClick={() => setIsMobileOpen(false)}
                        data-testid={c.testid}
                        className={`
                          flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium
                          transition-colors
                          ${
                            isActive
                              ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                              : 'text-white/70 hover:bg-white/10 hover:text-white'
                          }
                        `}
                      >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{c.name}</span>
                        {c.placeholder && (
                          <span
                            className="ml-auto text-[9px] uppercase tracking-wider text-muted-foreground/60"
                            title="Coming soon"
                          >
                            Soon
                          </span>
                        )}
                      </Link>
                    );
                  })}
              </div>
            )}
          </div>

          {/* User Info & Sign Out — on the navy panel */}
          <div className="border-t border-white/10 p-4">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p
                  className="text-sm font-medium text-white truncate"
                  data-testid="sidebar-user-email"
                >
                  {user?.email}
                </p>
                {isAdminUser && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                  Admin
                </span>
              )}
              </div>
              <TimezoneSelector />
            </div>
            <Button
              variant="outline"
              className="w-full justify-start bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
              onClick={handleSignOut}
              data-testid="sign-out-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
};
